import { docContentBaseUrl, DocumentService } from "@/services/api/DocumentService";
import { PDFService } from "@/services/knowledgebase/PDFService";
import type { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";

/**
 * Reads a stored document's extracted text.
 *
 * The same resolution rules already live inside `useDocumentContent` (a reducer
 * hook bound to the details drawer) and inside `useSendDocumentToElevenLabs`
 * (a callback), neither of which can be called for a plain "give me the text of
 * this document" read. This is that read, and nothing else.
 *
 * Resolution order matches the existing paths:
 *   1. `metadata.edited_url` — a hand-edited body wins.
 *   2. PDFs — `metadata.json` at `parsed_path`, then `parsed_<n>.txt` parts.
 *   3. Everything else — signed URL on `parsed_path`.
 */

const DEFAULT_MAX_CHARS = 6000;

export interface DocumentText {
  text: string;
  characters: number;
  truncated: boolean;
}

const cap = (text: string, maxChars: number): DocumentText => {
  const clean = text.trim();
  if (clean.length <= maxChars) {
    return { text: clean, characters: clean.length, truncated: false };
  }
  return {
    text: `${clean.slice(0, maxChars).trimEnd()}…`,
    characters: clean.length,
    truncated: true,
  };
};

const buildUrl = async (basePath: string, filePath: string): Promise<string | null> => {
  if (!basePath) return null;
  if (basePath.includes("fs.enterprisedna.co")) return `${basePath}/${filePath}`;
  return DocumentService.getSignedUrl(`${basePath}/${filePath}`);
};

const fetchPdfText = async (doc: DocumentWithMetadata, maxChars: number): Promise<string> => {
  const base = doc.parsed_path || (doc.id ? `${docContentBaseUrl}${doc.id}` : null);
  if (!base) return "";

  const metadataUrl = await buildUrl(base, "metadata.json");
  if (!metadataUrl) return "";

  const metadataResponse = await fetch(metadataUrl);
  if (!metadataResponse.ok) {
    const direct = await fetch(base);
    return direct.ok ? direct.text() : "";
  }

  const metadata = await metadataResponse.json();
  const totalParts = Number(metadata?.parts) || 1;
  const parts: string[] = [];

  for (let part = 1; part <= totalParts; part++) {
    if (parts.join("\n\n").length >= maxChars) break;
    const partUrl = await buildUrl(base, `parsed_${part}.txt`);
    if (!partUrl) break;
    const response = await fetch(partUrl);
    if (!response.ok) break;
    parts.push(await response.text());
  }

  return parts.join("\n\n");
};

export class DocumentTextService {
  static async getText(
    doc: DocumentWithMetadata,
    maxChars: number = DEFAULT_MAX_CHARS,
  ): Promise<DocumentText> {
    const editedUrl = doc.metadata?.edited_url;
    if (typeof editedUrl === "string" && editedUrl) {
      const response = await fetch(editedUrl);
      if (response.ok) return cap(await response.text(), maxChars);
    }

    if (PDFService.isPdf(doc)) {
      return cap(await fetchPdfText(doc, maxChars), maxChars);
    }

    if (!doc.parsed_path) return { text: "", characters: 0, truncated: false };

    const signedUrl = await DocumentService.getSignedUrl(doc.parsed_path);
    if (!signedUrl) return { text: "", characters: 0, truncated: false };

    const response = await fetch(signedUrl);
    if (!response.ok) return { text: "", characters: 0, truncated: false };

    return cap(await response.text(), maxChars);
  }
}
